import { Cancel, CheckCircle, Close, CloudUpload, Delete, Description, Info } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Popover,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface CVAnalysisDialogProps {
    open: boolean;
    onClose: () => void;
    onAnalysisComplete?: (results: any) => void;
}

interface SkillItem {
    id: number;
    name: string;
    status: 'pending' | 'accepted' | 'rejected';
    category: 'Soft Skills' | 'Hard Skills' | 'Industry Skills' | 'Functional Skills';
    level: 1 | 2 | 3 | 4;
}

interface SuggestionItem {
    id: number;
    name: string;
    status: 'pending' | 'accepted' | 'rejected';
    description?: string;
}

interface AnalysisResults {
    skills: SkillItem[];
    certificates: SuggestionItem[];
    roles: SuggestionItem[];
    description: string;
}

const skillLevels = [
    { level: 1, name: 'Junior', description: 'Grundlegendes Wissen und Fähigkeiten', color: '#e5f8f3' },
    {
        level: 2,
        name: 'Mid-Level',
        description: 'Solides Verständnis, Umfangreiche Kenntnisse in einem geschlossenen Kontext',
        color: '#ccf1e6',
    },
    {
        level: 3,
        name: 'Senior',
        description: 'Breites und tiefes Verständnis, Umfangreiche Kenntnisse in mehreren Kontexten',
        color: '#4dcea9',
    },
    {
        level: 4,
        name: 'Expert',
        description: 'Exzellentes Wissen und Fähigkeiten, Umfangreiche Kenntnisse in diversen Kontexten',
        color: '#04b884',
    },
];

const mockAnalysisResults = {
    skills: [
        { id: 1, name: 'React.js', status: 'pending' as const, category: 'Hard Skills' as const, level: 3 as const },
        { id: 2, name: 'TypeScript', status: 'pending' as const, category: 'Hard Skills' as const, level: 3 as const },
        { id: 3, name: 'Node.js', status: 'pending' as const, category: 'Hard Skills' as const, level: 2 as const },
        { id: 4, name: 'AWS', status: 'pending' as const, category: 'Industry Skills' as const, level: 2 as const },
        {
            id: 5,
            name: 'Kommunikation',
            status: 'pending' as const,
            category: 'Soft Skills' as const,
            level: 3 as const,
        },
    ],
    certificates: [
        { id: 1, name: 'AWS Certified Developer', status: 'pending' as const },
        { id: 2, name: 'React Professional Certificate', status: 'pending' as const },
    ],
    roles: [
        {
            id: 1,
            name: 'Senior Frontend Developer',
            status: 'pending' as const,
            description: 'Lead frontend development projects',
        },
        {
            id: 2,
            name: 'Full-Stack Developer',
            status: 'pending' as const,
            description: 'Work across frontend and backend technologies',
        },
    ],
    description: 'Erfahrener Entwickler mit Fokus auf moderne Web-Technologien',
};

export default function CVAnalysisDialog({ open, onClose, onAnalysisComplete }: CVAnalysisDialogProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [skills, setSkills] = useState<SkillItem[]>([]);
    const [certificates, setCertificates] = useState<SuggestionItem[]>([]);
    const [roles, setRoles] = useState<SuggestionItem[]>([]);

    const steps = ['CV Upload', 'Ergebnisse prüfen'];
    const infoOpen = Boolean(infoAnchorEl);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleFileUpload(e.target.files[0]);
        }
    };

    const handleFileUpload = async (file: File) => {
        setError(null);

        const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];

        if (!allowedTypes.includes(file.type)) {
            setError('Bitte wählen Sie eine PDF-, DOC- oder DOCX-Datei aus.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Die Datei ist zu groß. Maximale Größe: 10MB.');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const progressInterval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(progressInterval);
                    setUploadedFile(file);
                    setUploading(false);
                    setIsAnalyzing(true);

                    setTimeout(() => {
                        setAnalysisResults(mockAnalysisResults);
                        setSkills(mockAnalysisResults.skills);
                        setCertificates(mockAnalysisResults.certificates);
                        setRoles(mockAnalysisResults.roles);
                        setIsAnalyzing(false);
                        setCurrentStep(1);
                    }, 2000);

                    return 100;
                }
                return prev + 20;
            });
        }, 200);
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setUploadProgress(0);
        setError(null);
        setAnalysisResults(null);
        setIsAnalyzing(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleComplete = () => {
        const acceptedResults = {
            skills: skills.filter((s) => s.status === 'accepted'),
            certificates: certificates.filter((c) => c.status === 'accepted'),
            roles: roles.filter((r) => r.status === 'accepted'),
            description: analysisResults?.description || '',
        };

        if (onAnalysisComplete) {
            onAnalysisComplete(acceptedResults);
        }
        onClose();
    };

    const handleClose = () => {
        handleRemoveFile();
        setCurrentStep(0);
        setSkills([]);
        setCertificates([]);
        setRoles([]);
        onClose();
    };

    const handleStatusChange = (
        items: SkillItem[] | SuggestionItem[],
        setItems: React.Dispatch<React.SetStateAction<any>>,
        id: number,
        newStatus: 'accepted' | 'rejected' | 'pending',
    ) => {
        setItems((prev: any[]) => prev.map((item: any) => (item.id === id ? { ...item, status: newStatus } : item)));
    };

    const handleSkillLevelChange = (skillId: number, newLevel: 1 | 2 | 3 | 4) => {
        setSkills((prev) => prev.map((skill) => (skill.id === skillId ? { ...skill, level: newLevel } : skill)));
    };

    const handleInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setInfoAnchorEl(event.currentTarget);
    };

    const handleInfoClose = () => {
        setInfoAnchorEl(null);
    };

    const getSkillLevelInfo = (level: 1 | 2 | 3 | 4) => {
        return skillLevels.find((sl) => sl.level === level) || skillLevels[0];
    };

    const getTextColor = (level: number) => {
        return level >= 3 ? 'white' : 'text.primary';
    };

    const groupedSkills = skills.reduce(
        (acc, skill) => {
            if (!acc[skill.category]) {
                acc[skill.category] = [];
            }
            acc[skill.category].push(skill);
            return acc;
        },
        {} as Record<string, SkillItem[]>,
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            aria-labelledby="cv-analysis-dialog-title"
        >
            <DialogTitle id="cv-analysis-dialog-title">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        CV Analyse
                    </Typography>
                    <IconButton onClick={handleClose} aria-label="Dialog schließen">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {currentStep === 0 && (
                    <>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Laden Sie Ihren Lebenslauf hoch, damit unsere KI Ihre Fähigkeiten und Erfahrungen
                            analysieren kann.
                        </Typography>

                        <Card
                            elevation={0}
                            sx={{
                                mb: 4,
                                border: `2px dashed ${dragActive ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                                background: dragActive
                                    ? alpha(theme.palette.primary.main, 0.05)
                                    : alpha(theme.palette.background.paper, 0.7),
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    background: alpha(theme.palette.primary.main, 0.02),
                                },
                            }}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            aria-label="Datei-Upload-Bereich"
                        >
                            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                {!uploadedFile && !uploading && (
                                    <>
                                        <CloudUpload
                                            sx={{
                                                fontSize: 48,
                                                color: theme.palette.primary.main,
                                                mb: 2,
                                            }}
                                            aria-hidden="true"
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            Datei hier ablegen oder klicken zum Auswählen
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Unterstützte Formate: PDF, DOC, DOCX (max. 10MB)
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<CloudUpload />}
                                            size="medium"
                                            sx={{
                                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                            }}
                                            aria-label="Datei zum Upload auswählen"
                                        >
                                            Datei auswählen
                                        </Button>
                                    </>
                                )}

                                {(uploading || isAnalyzing) && (
                                    <Box>
                                        <CloudUpload
                                            sx={{
                                                fontSize: 48,
                                                color: theme.palette.primary.main,
                                                mb: 2,
                                                animation: 'pulse 2s ease-in-out infinite',
                                            }}
                                            aria-hidden="true"
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                            {isAnalyzing ? 'KI analysiert Ihren CV...' : 'Datei wird hochgeladen...'}
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                mb: 1,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 4,
                                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                                },
                                            }}
                                            aria-label={`Upload-Fortschritt: ${Math.round(uploadProgress)}%`}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            {Math.round(uploadProgress)}%
                                        </Typography>
                                    </Box>
                                )}

                                {uploadedFile && !uploading && !isAnalyzing && (
                                    <Box>
                                        <CheckCircle
                                            sx={{
                                                fontSize: 48,
                                                color: theme.palette.success.main,
                                                mb: 2,
                                            }}
                                            aria-hidden="true"
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {analysisResults
                                                ? 'Analyse abgeschlossen'
                                                : 'Datei erfolgreich hochgeladen'}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 1,
                                                mb: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Description color="primary" aria-hidden="true" />
                                            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                                {uploadedFile.name}
                                            </Typography>
                                            <Chip
                                                label={`${(uploadedFile.size / 1024 / 1024).toFixed(1)} MB`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>

                                        {analysisResults && (
                                            <>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        justifyContent: 'center',
                                                        mb: 3,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <Chip
                                                        label={`${analysisResults.skills.length} Skills`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                    <Chip
                                                        label={`${analysisResults.certificates.length} Zertifikate`}
                                                        size="small"
                                                        color="secondary"
                                                    />
                                                    <Chip
                                                        label={`${analysisResults.roles.length} Rollen`}
                                                        size="small"
                                                        color="info"
                                                    />
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => setCurrentStep(1)}
                                                    sx={{
                                                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                                        mb: 2,
                                                    }}
                                                >
                                                    Ergebnisse überprüfen
                                                </Button>
                                            </>
                                        )}

                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveFile();
                                            }}
                                            color="error"
                                            size="small"
                                            aria-label="Datei entfernen"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {error && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                action={
                                    <IconButton
                                        color="inherit"
                                        size="small"
                                        onClick={() => setError(null)}
                                        aria-label="Fehler schließen"
                                    >
                                        <Close />
                                    </IconButton>
                                }
                            >
                                {error}
                            </Alert>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            aria-label="CV-Datei auswählen"
                        />
                    </>
                )}

                {currentStep === 1 && analysisResults && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                            Analyse-Ergebnisse überprüfen
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                            Bitte überprüfen Sie die gefundenen Skills, Zertifikate und Rollen. Sie können diese
                            annehmen oder ablehnen und Skill-Level anpassen.
                        </Typography>

                        {/* Skills Section */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Gefundene Skills ({skills.length})
                                    </Typography>
                                    <IconButton size="small" onClick={handleInfoClick} aria-label="Skill-Level Info">
                                        <Info fontSize="small" />
                                    </IconButton>
                                    <Popover
                                        open={infoOpen}
                                        anchorEl={infoAnchorEl}
                                        onClose={handleInfoClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    >
                                        <Box sx={{ p: 2, maxWidth: 300 }}>
                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                Skills werden automatisch bewertet. Sie können die Level von 1-4
                                                anpassen:
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {skillLevels.map((level) => (
                                                    <Box
                                                        key={level.level}
                                                        sx={{
                                                            flex: '1 1 calc(50% - 4px)',
                                                            minWidth: 120,
                                                            p: 1,
                                                            borderRadius: 0.5,
                                                            backgroundColor: level.color,
                                                            color: getTextColor(level.level),
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                                                        >
                                                            {level.level}. {level.name}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Popover>
                                </Box>

                                {Object.entries(groupedSkills).map(([category, categorySkills]) => (
                                    <Box key={category} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                                            {category}
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Skill</TableCell>
                                                        <TableCell align="center">Level</TableCell>
                                                        <TableCell align="center">Anpassen</TableCell>
                                                        <TableCell align="center">Aktion</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {categorySkills.map((skill) => {
                                                        const levelInfo = getSkillLevelInfo(skill.level);
                                                        return (
                                                            <TableRow key={skill.id}>
                                                                <TableCell>{skill.name}</TableCell>
                                                                <TableCell align="center">
                                                                    <Chip
                                                                        label={levelInfo.name}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: levelInfo.color,
                                                                            color: getTextColor(skill.level),
                                                                            fontWeight: 500,
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <ButtonGroup size="small">
                                                                        {skillLevels.map((level) => (
                                                                            <Button
                                                                                key={level.level}
                                                                                onClick={() =>
                                                                                    handleSkillLevelChange(
                                                                                        skill.id,
                                                                                        level.level as 1 | 2 | 3 | 4,
                                                                                    )
                                                                                }
                                                                                variant={
                                                                                    skill.level === level.level
                                                                                        ? 'contained'
                                                                                        : 'outlined'
                                                                                }
                                                                                sx={{
                                                                                    minWidth: 32,
                                                                                    backgroundColor:
                                                                                        skill.level === level.level
                                                                                            ? level.color
                                                                                            : undefined,
                                                                                    color:
                                                                                        skill.level === level.level
                                                                                            ? getTextColor(level.level)
                                                                                            : 'text.primary',
                                                                                }}
                                                                            >
                                                                                {level.level}
                                                                            </Button>
                                                                        ))}
                                                                    </ButtonGroup>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            gap: 0.5,
                                                                            justifyContent: 'center',
                                                                        }}
                                                                    >
                                                                        {skill.status === 'pending' && (
                                                                            <>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{
                                                                                        color: '#0BB140',
                                                                                        borderColor: '#0BB140',
                                                                                    }}
                                                                                    onClick={() =>
                                                                                        handleStatusChange(
                                                                                            skills,
                                                                                            setSkills,
                                                                                            skill.id,
                                                                                            'accepted',
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    ✓
                                                                                </Button>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    color="primary"
                                                                                    onClick={() =>
                                                                                        handleStatusChange(
                                                                                            skills,
                                                                                            setSkills,
                                                                                            skill.id,
                                                                                            'rejected',
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    ✗
                                                                                </Button>
                                                                            </>
                                                                        )}
                                                                        {skill.status === 'accepted' && (
                                                                            <Chip
                                                                                icon={<CheckCircle />}
                                                                                label="Angenommen"
                                                                                size="small"
                                                                                sx={{
                                                                                    backgroundColor: '#0BB140',
                                                                                    color: 'white',
                                                                                }}
                                                                                onClick={() =>
                                                                                    handleStatusChange(
                                                                                        skills,
                                                                                        setSkills,
                                                                                        skill.id,
                                                                                        'pending',
                                                                                    )
                                                                                }
                                                                            />
                                                                        )}
                                                                        {skill.status === 'rejected' && (
                                                                            <Chip
                                                                                icon={<Cancel />}
                                                                                label="Abgelehnt"
                                                                                size="small"
                                                                                color="primary"
                                                                                onClick={() =>
                                                                                    handleStatusChange(
                                                                                        skills,
                                                                                        setSkills,
                                                                                        skill.id,
                                                                                        'pending',
                                                                                    )
                                                                                }
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Certificates Section */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Gefundene Zertifikate ({certificates.length})
                                </Typography>
                                <List>
                                    {certificates.map((cert) => (
                                        <ListItem key={cert.id} sx={{ px: 0, py: 1 }}>
                                            <ListItemText primary={cert.name} sx={{ mr: 2 }} />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {cert.status === 'pending' && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ color: '#0BB140', borderColor: '#0BB140' }}
                                                            onClick={() =>
                                                                handleStatusChange(
                                                                    certificates,
                                                                    setCertificates,
                                                                    cert.id,
                                                                    'accepted',
                                                                )
                                                            }
                                                        >
                                                            Annehmen
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() =>
                                                                handleStatusChange(
                                                                    certificates,
                                                                    setCertificates,
                                                                    cert.id,
                                                                    'rejected',
                                                                )
                                                            }
                                                        >
                                                            Ablehnen
                                                        </Button>
                                                    </>
                                                )}
                                                {cert.status === 'accepted' && (
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label="Angenommen"
                                                        size="small"
                                                        sx={{ backgroundColor: '#0BB140', color: 'white' }}
                                                        onClick={() =>
                                                            handleStatusChange(
                                                                certificates,
                                                                setCertificates,
                                                                cert.id,
                                                                'pending',
                                                            )
                                                        }
                                                    />
                                                )}
                                                {cert.status === 'rejected' && (
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label="Abgelehnt"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() =>
                                                            handleStatusChange(
                                                                certificates,
                                                                setCertificates,
                                                                cert.id,
                                                                'pending',
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>

                        {/* Roles Section */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Vorgeschlagene Rollen ({roles.length})
                                </Typography>
                                <List>
                                    {roles.map((role) => (
                                        <ListItem key={role.id} sx={{ px: 0, py: 1 }}>
                                            <ListItemText
                                                primary={role.name}
                                                secondary={role.description}
                                                sx={{ mr: 2 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {role.status === 'pending' && (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ color: '#0BB140', borderColor: '#0BB140' }}
                                                            onClick={() =>
                                                                handleStatusChange(roles, setRoles, role.id, 'accepted')
                                                            }
                                                        >
                                                            Annehmen
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() =>
                                                                handleStatusChange(roles, setRoles, role.id, 'rejected')
                                                            }
                                                        >
                                                            Ablehnen
                                                        </Button>
                                                    </>
                                                )}
                                                {role.status === 'accepted' && (
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label="Angenommen"
                                                        size="small"
                                                        sx={{ backgroundColor: '#0BB140', color: 'white' }}
                                                        onClick={() =>
                                                            handleStatusChange(roles, setRoles, role.id, 'pending')
                                                        }
                                                    />
                                                )}
                                                {role.status === 'rejected' && (
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label="Abgelehnt"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() =>
                                                            handleStatusChange(roles, setRoles, role.id, 'pending')
                                                        }
                                                    />
                                                )}
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button onClick={handleClose} variant="outlined">
                    Abbrechen
                </Button>
                {currentStep === 1 && (
                    <>
                        <Button onClick={() => setCurrentStep(0)} variant="outlined">
                            Zurück
                        </Button>
                        <Button
                            onClick={handleComplete}
                            variant="contained"
                            sx={{
                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                            }}
                        >
                            Ergebnisse übernehmen
                        </Button>
                    </>
                )}
                {currentStep === 1 && (
                    <Button
                        onClick={handleComplete}
                        variant="contained"
                        sx={{
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        }}
                    >
                        Ergebnisse übernehmen
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
